import m, {VnodeDOM} from "mithril";
import ICustomForm from "../../ICustomForm";

import ICustomFormSettings from "../../ICustomFormSettings";
import Calendar from "../Calendar";
import flatpickr from "flatpickr";
import {Czech} from "flatpickr/dist/l10n/cs";
import {Slovak} from "flatpickr/dist/l10n/sk";
import {Polish} from "flatpickr/dist/l10n/pl";
import {Hungarian} from "flatpickr/dist/l10n/hu";
import {German} from "flatpickr/dist/l10n/de";
import {Thai} from "flatpickr/dist/l10n/th";


import {key as LocaleKeyType} from "flatpickr/dist/types/locale";
import {BaseOptions} from "flatpickr/dist/types/options";
import WeekSelect from "flatpickr/dist/plugins/weekSelect/weekSelect"
import moment, {Moment} from "moment";
import CustomLocale = flatpickr.CustomLocale;

export default class CalendarPicker extends Calendar implements ICustomForm
{
    private static readonly DATE_FORMAT_PICKER = 'Y-m-d';
    private static readonly DATETIME_FORMAT_PICKER = 'Y-m-d H:i';
    private static readonly DATE_FORMAT_MOMENT = 'YYYY-MM-DD';
    private static readonly DATETIME_FORMAT_MOMENT = 'YYYY-MM-DD HH:mm';

    private picker?: flatpickr.Instance;

    constructor(settings: ICustomFormSettings) {
        super(settings);
    }

    private getHintMessage() {
        const messages = this.settings.form.questionary.messages;
        switch (this.calendarConfig.range) {
            case "datetime": return messages.get('pickDatetime');
            case "day": return messages.get('pickDay');
            case "week": return messages.get('pickWeek');
            case "custom": return messages.get('pickRange');
        }
    }

    // Sorry, I not able to write word "localization" without dictionary.
    private getL10n(): CustomLocale|undefined {
        switch (this.locale) {
            case "cs": return Czech;
            case "de": return German;
            case "hu": return Hungarian;
            case "pl": return Polish;
            case "sk": return Slovak;
            case "th": return Thai;
            default: return undefined; // English
        }
    }

    private get locale(): LocaleKeyType {
        return <LocaleKeyType>(this.calendarConfig.locale || this.settings.form.questionary.guessLocale());
    }

    private toBudThai(date: Moment): Moment {
        // Simple year manipulation doesn't work because Flatpicker moves to this year and days do not match with origin year
        return date; 
        // let yearChrist = date.format('YYYY');
        // let dateChrist = date.format('YYYY-MM-DD HH:mm:ss');
        //
        // let yearBud = (parseInt(yearChrist) + 543).toString();
        // let dateBud = dateChrist.replace(yearChrist, yearBud);
        //
        // return moment(dateBud);
    }

    private fromBudThai(date: Moment): Moment {
        // Simple year manipulation doesn't work because Flatpicker moves to this year and days do not match with origin year
        return date;
        // let yearBud = date.format('YYYY');
        // let dateBud = date.format('YYYY-MM-DD HH:mm:ss');
        //
        // let yearChrist = (parseInt(yearBud) - 543).toString();
        // let dateChrist = dateBud.replace(yearBud, yearChrist);
        //
        // return moment(dateChrist);     
    }

    private get pickerFormat(): string {
        return this.calendarConfig.range === "datetime" ? CalendarPicker.DATETIME_FORMAT_PICKER : CalendarPicker.DATE_FORMAT_PICKER;
    }
    private get momentFormat(): string {
        return this.calendarConfig.range === "datetime" ? CalendarPicker.DATETIME_FORMAT_MOMENT : CalendarPicker.DATE_FORMAT_MOMENT;
    }

    private getDefaultDate(): Moment {
        const date = this.beginDateInput.getVariable() ? moment(this.beginDateInput.getVariable(), this.momentFormat) : moment();
        return (this.locale === 'th') ? this.toBudThai(date) : date;
    }

    private getMinDate(): string|undefined {
        if (this.locale === 'th' && this.calendarConfig.minDate) {
            return this.toBudThai(this.calendarConfig.minDate).format(this.momentFormat);
        }
        return this.calendarConfig.minDate?.format(this.momentFormat);
    }

    private getMaxDate(): string|undefined {
        if (this.locale === 'th' && this.calendarConfig.maxDate) {
            return this.toBudThai(this.calendarConfig.maxDate).format(this.momentFormat);
        }
        return this.calendarConfig.maxDate?.format(this.momentFormat);
    }

    private savePicked(begin: Moment, end: Moment): void {
        let beginFormatted, endFormatted;
        if (this.locale === 'th') {
            beginFormatted = this.fromBudThai(begin).format(this.momentFormat);
            endFormatted = this.fromBudThai(end).format(this.momentFormat);
        } else {
            beginFormatted = begin.format(this.momentFormat);
            endFormatted = end.format(this.momentFormat);
        }
        this.beginDateInput.set(beginFormatted);
        this.endDateInput.set(endFormatted);
    }

    public view() {
        return this.viewLayout(
            m('form', {}, [
                m('p.hint', {}, this.getHintMessage()),
                m('input', {type: 'date'}, []),
                ...this.checkboxes.map(ch => m(ch)),
            ])
        );
    }

    oncreate(vnode: VnodeDOM) {
        super.oncreate(vnode);

        const input = $(vnode.dom).find("input[type='date']")[0];
        const options: Partial<BaseOptions> = {
            inline: true,
            locale: this.getL10n() || "en",
            minDate: this.getMinDate(),
            maxDate: this.getMaxDate(),
            dateFormat: this.pickerFormat,
            defaultDate: this.getDefaultDate().format(this.momentFormat),
            enableTime: this.calendarConfig.range === 'datetime',
        };

        let selectedDate;
        if (this.calendarConfig.range === "day" || this.calendarConfig.range === "datetime") {
            options.onChange = (selectedDates, dateStr, instance) => {
                this.beginDateInput.set(dateStr);
                this.endDateInput.set(dateStr);
            };
            selectedDate = this.beginDateInput.getVariable() || undefined;
        } else if (this.calendarConfig.range === "week") {
            // @ts-ignore
            options.plugins = [new WeekSelect({})];
            options.onChange = (selectedDates, dateStr, instance) => {
                if (selectedDates.length) {
                    this.savePicked(
                        moment(selectedDates[0]).startOf("isoWeek"),
                        moment(selectedDates[selectedDates.length-1]).endOf("isoWeek")
                    );
                }
            };
            selectedDate = this.beginDateInput.getVariable() || undefined;
        } else if (this.calendarConfig.range === "custom") {
            options.mode = 'range';
            options.onChange = (selectedDates, dateStr, instance) => {
                if (selectedDates.length) {
                    this.savePicked( moment(selectedDates[0]), moment(selectedDates[selectedDates.length-1]) );
                }
            };
            selectedDate = this.beginDateInput.getVariable() && this.endDateInput.getVariable() ? [this.beginDateInput.getVariable(), this.endDateInput.getVariable()] : undefined;
        }

        this.picker = <flatpickr.Instance>flatpickr(input, options);
        if (selectedDate) {
            this.picker.set('maxDate', this.getMaxDate());
            this.picker.setDate(selectedDate, false, this.pickerFormat);
        }
    }

    onupdate() {
        if (this.picker && !this.beginDateInput.getVariable() && this.settings.form.activeGlobalUnknown) {
            this.picker.clear();
        }
    }


}
